import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { ObligationDTO } from '@almanac/shared';
import { EditDrawer } from '../components/EditDrawer.js';
import { LoadStatus } from '../components/LoadStatus.js';
import { ObligationListItem } from '../components/ObligationListItem.js';
import { ObligationPreview } from '../components/ObligationPreview.js';
import { OccasionSuggestionBanner } from '../components/OccasionSuggestionBanner.js';
import { QuickAddBar } from '../components/QuickAddBar.js';
import {
  activeOccasionsByDue,
  areaColorVar,
  groupByArea,
  groupByPerson,
  groupForFocus,
  liveObligationTitle,
  monthProgress,
  obligationMatchesQuery,
  obligationSortKey,
  upcomingOccasions,
  useLifeAreas,
  useNowMinute,
  useObligations,
} from '../lib/obligations.js';
import { PersonAvatar } from '../components/PersonAvatar.js';
import { personInitials, usePeople } from '../lib/people.js';

/** The page's perspectives. Focus is the daily driver (horizons +
 *  the occasions strip); Months is the browse view the page has
 *  always had; Areas and People regroup the same rows. */
type Perspective = 'focus' | 'months' | 'areas' | 'people';
const PERSPECTIVES: { key: Perspective; label: string }[] = [
  { key: 'focus', label: 'Focus' },
  { key: 'months', label: 'Months' },
  { key: 'areas', label: 'Areas' },
  { key: 'people', label: 'People' },
];
const VIEW_STORAGE_KEY = 'almanac.obligations.view';

function storedPerspective(): Perspective {
  try {
    const v = localStorage.getItem(VIEW_STORAGE_KEY);
    if (v && PERSPECTIVES.some((p) => p.key === v)) return v as Perspective;
  } catch {
    /* storage unavailable — fall through to the default */
  }
  return 'focus';
}

export function ObligationsPage() {
  const obligations = useObligations();
  const lifeAreas = useLifeAreas();
  const people = usePeople();
  // Search runs client-side over the in-memory obligations cache,
  // matching the live title (person names included for occasions),
  // description, notes, and occasion label. The bar is hidden by
  // default behind a magnifying-glass icon in the page header —
  // search is a "sometimes" action for this scale of list, not a
  // permanent piece of chrome. `searchOpen` controls whether the
  // input renders; `searchQuery` is the value. Clearing the input
  // via × clears the query AND collapses the bar back to the icon
  // in one tap.
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  // Two surfaces, one id at a time each. Tapping a row sets
  // `previewingId` → the centered preview card opens. The card's
  // Edit button is a close-then-open: it clears `previewingId` and
  // sets `editingId`, which mounts the existing bottom-sheet drawer.
  // Keeping the two states separate (rather than a single enum) lets
  // the QuickAddBar's "Open existing" link route directly to the
  // drawer if we want it to in future without disturbing preview.
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Completed pile is a sliding history of "what I've done" — useful
  // to scan back through, but it grows unbounded over time. Default
  // it collapsed and don't persist the open state across sessions:
  // each visit is a fresh glance at active work; opening the drawer
  // is a deliberate "let me look back" gesture.
  const [completedExpanded, setCompletedExpanded] = useState(false);

  // Focus the input the moment it appears so the user can start
  // typing without an extra tap. Runs once per open transition.
  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  // Deep-link from a person card's occasion row: /obligations?open=<id>
  // opens that entry's preview. Waits for the list so the id resolves,
  // then consumes the param.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId || !obligations.data) return;
    if (obligations.data.some((o) => o.id === openId)) setPreviewingId(openId);
    const next = new URLSearchParams(searchParams);
    next.delete('open');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, obligations.data]);

  function closeSearch() {
    setSearchOpen(false);
    setSearchQuery('');
  }

  const filtered = useMemo(() => {
    const all = obligations.data ?? [];
    const trimmed = searchQuery.trim();
    if (!trimmed) return all;
    return all.filter((o) => obligationMatchesQuery(o, trimmed, people.data));
  }, [obligations.data, people.data, searchQuery]);
  const grouped = useMemo(() => groupByMonth(filtered), [filtered]);
  const isSearching = searchQuery.trim().length > 0;
  const noMatches = isSearching && grouped.length === 0;
  const areasData = lifeAreas.data;
  const areaById = useMemo(() => {
    const map = new Map<string, NonNullable<typeof areasData>[number]>();
    for (const a of areasData ?? []) map.set(a.id, a);
    return map;
  }, [areasData]);
  // Each life area takes an identity colour by its list position —
  // the same dot shows in row meta lines and Areas-view headings.
  const areaColorById = useMemo(() => {
    const map = new Map<string, string>();
    (areasData ?? []).forEach((a, i) => map.set(a.id, areaColorVar(i)));
    return map;
  }, [areasData]);

  // ── Perspective (Focus / Months / Areas / People) ────────────────
  // Sticky per device: the page reopens in whichever view was last
  // used. Search overrides the perspective while active — results
  // always render as the flat month list, whatever tab is selected.
  const [view, setView] = useState<Perspective>(storedPerspective);
  useEffect(() => {
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, view);
    } catch {
      /* storage unavailable — the choice just won't persist */
    }
  }, [view]);
  const showMonths = view === 'months' || isSearching;

  const nowMs = useNowMinute();
  const focusData = useMemo(() => {
    const all = obligations.data ?? [];
    const now = new Date(nowMs);
    return {
      groups: groupForFocus(all, now),
      strip: upcomingOccasions(all, now, 5),
      progress: monthProgress(all, now),
    };
  }, [obligations.data, nowMs]);
  const areaGroups = useMemo(
    () => groupByArea(obligations.data ?? [], areasData ?? []),
    [obligations.data, areasData],
  );
  const areaOccasions = useMemo(
    () => activeOccasionsByDue(obligations.data ?? []),
    [obligations.data],
  );
  const peopleData = people.data;
  const personGroups = useMemo(
    () => groupByPerson(obligations.data ?? [], peopleData ?? []),
    [obligations.data, peopleData],
  );
  const personById = useMemo(() => {
    const map = new Map<string, NonNullable<typeof peopleData>[number]>();
    for (const p of peopleData ?? []) map.set(p.id, p);
    return map;
  }, [peopleData]);

  const previewing = obligations.data?.find((o) => o.id === previewingId) ?? null;
  const editing = obligations.data?.find((o) => o.id === editingId) ?? null;

  // Rail stats for the desktop "At a glance" card. Counts run over the
  // full list (not the search-filtered view) so they describe the
  // user's real load regardless of what's typed in the box.
  const glance = useMemo(() => {
    const active = (obligations.data ?? []).filter((o) => o.status !== 'completed');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const plus = (days: number) => {
      const d = new Date(today);
      d.setDate(d.getDate() + days);
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    };
    const fortnight = plus(14);
    const quarter = plus(90);
    const dueOf = (o: ObligationDTO) => (o.nextDueDate ?? o.dueDate)?.slice(0, 10) ?? '';
    return {
      active: active.length,
      // ≤ the cutoff (not a from–to window) so overdue items count as
      // "due now" rather than silently dropping out of the number.
      dueSoon: active.filter((o) => dueOf(o) !== '' && dueOf(o) <= fortnight).length,
      occasions: active.filter((o) => o.kind === 'occasion' && dueOf(o) <= quarter).length,
    };
  }, [obligations.data]);

  // Months-index click: scroll the matching group into view. Jumping
  // to Completed also expands the drawer first, then scrolls once the
  // body has rendered.
  function jumpToGroup(key: string) {
    if (key === 'completed') setCompletedExpanded(true);
    requestAnimationFrame(() => {
      document
        .getElementById(`obl-month-${key}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  return (
    <section>
      <header className="page-header page-header--with-action">
        <div className="page-header__lead">
          <Link
            to="/"
            className="page-header__icon-button page-header__back"
            aria-label="Back to home"
          >
            <BackIcon />
          </Link>
          <div>
            <div className="section__label">All obligations</div>
            <h2 className="page-header__title">Obligations</h2>
          </div>
        </div>
        {/* The header toggle and its collapsible bar are phone chrome;
            on desktop the rail carries a permanent search box, so both
            hide behind .mobile-only there. */}
        <button
          type="button"
          className={`page-header__icon-button mobile-only ${searchOpen ? 'page-header__icon-button--active' : ''}`}
          onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}
          aria-label={searchOpen ? 'Close search' : 'Search obligations'}
          aria-expanded={searchOpen}
        >
          <SearchIcon />
        </button>
      </header>

      <div className="page-columns">
      <div className="page-measure">
      <QuickAddBar onEditExisting={setPreviewingId} />

      {searchOpen && (
        <div className="obligations-search mobile-only">
          <input
            ref={searchInputRef}
            type="search"
            className="obligations-search__input"
            placeholder="Search by title, notes, or person…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') closeSearch();
            }}
            aria-label="Search obligations"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <button
            type="button"
            className="obligations-search__clear"
            onClick={closeSearch}
            aria-label="Close search"
          >
            ×
          </button>
        </div>
      )}

      <OccasionSuggestionBanner onOpen={setPreviewingId} />

      <LoadStatus
        isLoading={obligations.isLoading}
        isError={obligations.isError}
        error={obligations.error}
      />

      {!isSearching && (
        <div className="lens-switch" role="tablist" aria-label="View obligations as">
          {PERSPECTIVES.map((p) => (
            <button
              key={p.key}
              type="button"
              role="tab"
              aria-selected={view === p.key}
              className={`lens-switch__option ${view === p.key ? 'lens-switch__option--active' : ''}`}
              onClick={() => setView(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}

      {noMatches && obligations.isSuccess && (
        <p className="empty-state">
          No matches for &ldquo;{searchQuery.trim()}&rdquo;.
        </p>
      )}

      {!isSearching && grouped.length === 0 && obligations.isSuccess && (
        <p className="empty-state">
          Nothing yet. Use the form above to add an obligation — pick a category and
          a due date, then press Add.
        </p>
      )}

      {/* ── FOCUS — horizons + the occasions strip ──────────────── */}
      {!isSearching && view === 'focus' && grouped.length > 0 && (
        <>
          {focusData.progress && (
            <div className="month-progress">
              <span className="month-progress__label">
                {focusData.progress.label} · {focusData.progress.done} of{' '}
                {focusData.progress.total} done
              </span>
              <div className="month-progress__track" aria-hidden>
                <div
                  className="month-progress__fill"
                  style={{
                    width: `${Math.round((focusData.progress.done / focusData.progress.total) * 100)}%`,
                  }}
                />
              </div>
            </div>
          )}
          {focusData.strip.length > 0 && (
            <div className="occasion-strip" aria-label="Coming up">
              {focusData.strip.map((o) => {
                const person = o.personIds[0] ? personById.get(o.personIds[0]) : undefined;
                const title = liveObligationTitle(o, people.data);
                return (
                  <button
                    key={o.id}
                    type="button"
                    className="occasion-card"
                    onClick={() => setPreviewingId(o.id)}
                  >
                    {person ? (
                      <PersonAvatar person={person} className="occasion-card__av" />
                    ) : (
                      <span className="occasion-card__av" aria-hidden>
                        {personInitials(title)}
                      </span>
                    )}
                    <span className="occasion-card__text">
                      <span className="occasion-card__name">
                        {person ? person.displayName.split(' ')[0] : title}
                      </span>
                      <span className="occasion-card__sub">{occasionStripSub(o)}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="month-groups">
            {(
              [
                { key: 'overdue', label: 'Overdue', rows: focusData.groups.overdue },
                { key: 'week', label: 'This week', rows: focusData.groups.thisWeek },
                { key: 'later', label: 'Later', rows: focusData.groups.later },
              ] as const
            )
              .filter((s) => s.rows.length > 0)
              .map((s) => (
                <section key={s.key} className="month-group">
                  <h3
                    className={`month-group__heading ${s.key === 'overdue' ? 'month-group__heading--overdue' : ''}`}
                  >
                    {s.label}
                    <span className="month-group__count"> · {s.rows.length}</span>
                  </h3>
                  <div className="list" role="list">
                    {s.rows.map((o) => (
                      <ObligationListItem
                        key={o.id}
                        obligation={o}
                        lifeArea={areaById.get(o.lifeAreaId)}
                        areaColor={areaColorById.get(o.lifeAreaId)}
                        onEdit={setPreviewingId}
                      />
                    ))}
                  </div>
                </section>
              ))}
          </div>
        </>
      )}

      {/* ── AREAS — tasks regrouped by life area; occasions cluster
             separately below (nearly all file under Personal, which
             would otherwise flatten that group back into a pile). ── */}
      {!isSearching && view === 'areas' && (
        <div className="month-groups">
          {areaGroups.map((g) => (
            <section key={g.areaId} className="month-group">
              <h3 className="month-group__heading">
                <span
                  className="area-dot"
                  style={{ background: areaColorVar(g.colorIndex) }}
                  aria-hidden
                />
                {g.label}
                <span className="month-group__count"> · {g.rows.length}</span>
              </h3>
              <div className="list" role="list">
                {g.rows.map((o) => (
                  <ObligationListItem
                    key={o.id}
                    obligation={o}
                    lifeArea={areaById.get(o.lifeAreaId)}
                    hideLifeArea
                    onEdit={setPreviewingId}
                  />
                ))}
              </div>
            </section>
          ))}
          {areaOccasions.length > 0 && (
            <section className="month-group">
              <h3 className="month-group__heading">
                Birthdays &amp; anniversaries
                <span className="month-group__count"> · {areaOccasions.length}</span>
              </h3>
              <div className="list list--occasions" role="list">
                {areaOccasions.map((o) => (
                  <ObligationListItem
                    key={o.id}
                    obligation={o}
                    lifeArea={areaById.get(o.lifeAreaId)}
                    onEdit={setPreviewingId}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {/* ── PEOPLE — the household's dates, person by person ─────── */}
      {!isSearching && view === 'people' && (
        <div className="month-groups">
          {personGroups.length === 0 && grouped.length > 0 && obligations.isSuccess && (
            <p className="empty-state">
              No occasions yet. Add a birthday or anniversary from a person&rsquo;s card
              in the People tool, and it shows here.
            </p>
          )}
          {personGroups.map((g) => (
            <section key={g.personId} className="month-group">
              <h3 className="month-group__heading">
                {g.label}
                <span className="month-group__count"> · {g.rows.length}</span>
              </h3>
              <div className="list list--occasions" role="list">
                {g.rows.map((o) => (
                  <ObligationListItem
                    key={o.id}
                    obligation={o}
                    lifeArea={areaById.get(o.lifeAreaId)}
                    onEdit={setPreviewingId}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* ── MONTHS — the original browse (also hosts search results) ── */}
      {showMonths && (
      <div className="month-groups">
        {grouped.map((group) => {
          const isCompleted = group.key === 'completed';
          const total = group.tasks.length + group.occasions.length;
          // Completed group renders as a collapsible drawer. Active
          // month groups always render expanded with the heading as
          // a plain caption.
          const headingContent = isCompleted ? (
            <button
              type="button"
              className="month-group__heading month-group__heading--toggle"
              onClick={() => setCompletedExpanded((v) => !v)}
              aria-expanded={completedExpanded}
            >
              <span
                className={`month-group__chevron ${completedExpanded ? 'month-group__chevron--open' : ''}`}
                aria-hidden
              >
                ▸
              </span>
              {group.label}
              <span className="month-group__count">· {total}</span>
            </button>
          ) : (
            <h3 className="month-group__heading">
              {group.label}
              <span className="month-group__count"> · {total}</span>
            </h3>
          );
          // While searching, the completed pile auto-expands so the
          // user can see matches inside it. Without this, a match in
          // a row from a month ago would silently hide behind the
          // collapsed drawer and the user would conclude there were
          // no matches even when there are.
          const showBody = !isCompleted || completedExpanded || isSearching;
          // Inside the Completed drawer the recency is the story, so its
          // rows re-merge and sub-group by when they were finished —
          // This week / This month / Older — instead of the task/
          // occasion split the active months use.
          if (isCompleted) {
            return (
              <section key={group.key} id={`obl-month-${group.key}`} className="month-group">
                {headingContent}
                {showBody &&
                  completedBuckets([...group.tasks, ...group.occasions]).map((bucket) => (
                    <div key={bucket.label}>
                      <div className="month-group__subheading">
                        {bucket.label} · {bucket.rows.length}
                      </div>
                      <div className="list" role="list">
                        {bucket.rows.map((o) => (
                          <ObligationListItem
                            key={o.id}
                            obligation={o}
                            lifeArea={areaById.get(o.lifeAreaId)}
                            onEdit={setPreviewingId}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
              </section>
            );
          }
          return (
            <section key={group.key} id={`obl-month-${group.key}`} className="month-group">
              {headingContent}
              {showBody && group.tasks.length > 0 && (
                <div className="list" role="list">
                  {group.tasks.map((o) => (
                    <ObligationListItem
                      key={o.id}
                      obligation={o}
                      lifeArea={areaById.get(o.lifeAreaId)}
                      areaColor={areaColorById.get(o.lifeAreaId)}
                      onEdit={setPreviewingId}
                    />
                  ))}
                </div>
              )}
              {showBody && group.occasions.length > 0 && (
                <>
                  {/* Sub-heading only appears when there are occasions in
                      this month. The label is muted so it groups the rows
                      without competing with the month heading above. For
                      months that have both kinds, the task list and the
                      occasion list are visually distinct clusters; for
                      months that have only one kind, the sub-heading is
                      skipped entirely to avoid implying "section with no
                      items in the other half". */}
                  {group.tasks.length > 0 && (
                    <div className="month-group__subheading">People</div>
                  )}
                  <div className="list list--occasions" role="list">
                    {group.occasions.map((o) => (
                      <ObligationListItem
                        key={o.id}
                        obligation={o}
                        lifeArea={areaById.get(o.lifeAreaId)}
                        onEdit={setPreviewingId}
                      />
                    ))}
                  </div>
                </>
              )}
            </section>
          );
        })}
      </div>
      )}

      </div>

      {/* Desktop-only contextual rail (display:none below 840px):
          permanent search, a months jump-index, and load-at-a-glance. */}
      <aside className="page-rail">
        <div className="obligations-search">
          <input
            type="search"
            className="obligations-search__input"
            placeholder="Search by title, notes, or person…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search obligations"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          {searchQuery !== '' && (
            <button
              type="button"
              className="obligations-search__clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>

        {showMonths && grouped.length > 0 && (
          <nav className="rail-card" aria-label="Jump to month">
            <div className="doc-seclabel">Months</div>
            {grouped.map((group, i) => (
              <button
                key={group.key}
                type="button"
                className={`rail-nav__item ${i === 0 && group.key !== 'completed' ? 'rail-nav__item--current' : ''}`}
                onClick={() => jumpToGroup(group.key)}
              >
                <span className="rail-nav__label">
                  <span className="rail-nav__dot" aria-hidden />
                  {group.label}
                </span>
                <span className="rail-nav__count">
                  {group.tasks.length + group.occasions.length}
                </span>
              </button>
            ))}
          </nav>
        )}

        {obligations.isSuccess && glance.active > 0 && (
          <div className="rail-card">
            <div className="doc-seclabel">At a glance</div>
            <div className="rail-glance">
              <div className="rail-glance__line">
                <strong>{glance.active}</strong> active{' '}
                {glance.active === 1 ? 'obligation' : 'obligations'}
              </div>
              {glance.dueSoon > 0 && (
                <div className="rail-glance__line">
                  <strong>{glance.dueSoon}</strong> due in the next two weeks
                </div>
              )}
              {glance.occasions > 0 && (
                <div className="rail-glance__line">
                  <strong>{glance.occasions}</strong>{' '}
                  {glance.occasions === 1 ? 'occasion' : 'occasions'} in the next three months
                </div>
              )}
            </div>
          </div>
        )}
      </aside>
      </div>

      {previewing && (
        <ObligationPreview
          obligation={previewing}
          lifeArea={areaById.get(previewing.lifeAreaId)}
          lifeAreas={areasData ?? []}
          onClose={() => setPreviewingId(null)}
          onEdit={() => {
            // Close-then-open: clearing the preview state unmounts
            // the centered card before the drawer mounts, so the two
            // surfaces never overlap.
            const id = previewing.id;
            setPreviewingId(null);
            setEditingId(id);
          }}
        />
      )}

      <EditDrawer obligation={editing} onClose={() => setEditingId(null)} />
    </section>
  );
}

interface MonthGroup {
  key: string;
  label: string;
  // Tasks and occasions render in separate sub-lists within the same
  // month so birthdays cluster as a glance-able group rather than
  // being interspersed with the dated todos around them.
  tasks: ObligationDTO[];
  occasions: ObligationDTO[];
}

function groupByMonth(rows: ObligationDTO[]): MonthGroup[] {
  const active = rows.filter((r) => r.status !== 'completed');
  const completed = rows.filter((r) => r.status === 'completed');
  const groups = new Map<string, MonthGroup>();

  for (const o of active) {
    const due = o.nextDueDate ?? o.dueDate;
    const d = due ? new Date(`${due.slice(0, 10)}T00:00:00`) : null;
    let key: string;
    let label: string;
    if (!d || Number.isNaN(d.getTime())) {
      key = 'unscheduled';
      label = 'Unscheduled';
    } else {
      key = `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
      label = d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    }
    let group = groups.get(key);
    if (!group) {
      group = { key, label, tasks: [], occasions: [] };
      groups.set(key, group);
    }
    if (o.kind === 'occasion') group.occasions.push(o);
    else group.tasks.push(o);
  }

  const sorted = [...groups.values()].sort((a, b) => {
    if (a.key === 'unscheduled') return 1;
    if (b.key === 'unscheduled') return -1;
    return a.key.localeCompare(b.key);
  });

  // Date + time-of-day ascending sort within each month. Two items
  // on the same calendar day order by their `dueTime`, so a 9 AM
  // commitment surfaces above an 11 AM one; untimed rows fall
  // after timed rows of the same date. Recurring rows acknowledged
  // today carry a bumped `nextDueDate` (server + optimistic), so
  // they drift to their next occurrence naturally without a
  // separate sink rule. Fully-completed one-offs go into the
  // separate "Completed" pseudo-month below and don't appear in
  // regular months at all.
  function byDueDate(a: ObligationDTO, b: ObligationDTO) {
    return obligationSortKey(a).localeCompare(obligationSortKey(b));
  }
  for (const g of sorted) {
    g.tasks.sort(byDueDate);
    g.occasions.sort(byDueDate);
  }

  if (completed.length) {
    // Completed sits in its own pseudo-month at the bottom. Mixing
    // task/occasion completions there is fine — once something is
    // done, the distinction is less interesting than the recency.
    const completedTasks = completed.filter((o) => o.kind !== 'occasion');
    const completedOccasions = completed.filter((o) => o.kind === 'occasion');
    const byCompleted = (a: ObligationDTO, b: ObligationDTO) =>
      (b.completedAt ?? '').localeCompare(a.completedAt ?? '');
    sorted.push({
      key: 'completed',
      label: 'Completed',
      tasks: completedTasks.sort(byCompleted),
      occasions: completedOccasions.sort(byCompleted),
    });
  }

  return sorted;
}

/** Sub-group the Completed drawer's rows by how recently they were
 *  finished. Empty buckets drop out; ordering is newest bucket first,
 *  newest completion first within each. */
function completedBuckets(rows: ObligationDTO[]): { label: string; rows: ObligationDTO[] }[] {
  const sorted = [...rows].sort((a, b) =>
    (b.completedAt ?? '').localeCompare(a.completedAt ?? ''),
  );
  const now = Date.now();
  const day = 1000 * 60 * 60 * 24;
  const buckets: { label: string; rows: ObligationDTO[] }[] = [
    { label: 'This week', rows: [] },
    { label: 'This month', rows: [] },
    { label: 'Older', rows: [] },
  ];
  for (const o of sorted) {
    const done = o.completedAt ? new Date(o.completedAt).getTime() : 0;
    const age = now - done;
    if (done && age <= 7 * day) buckets[0]!.rows.push(o);
    else if (done && age <= 30 * day) buckets[1]!.rows.push(o);
    else buckets[2]!.rows.push(o);
  }
  return buckets.filter((b) => b.rows.length > 0);
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** The occasion-strip card's one-line subtitle — "Turns 36 · 1mo",
 *  "Wedding · 12y · 3mo". Same age arithmetic as the row meta (the
 *  next occurrence's year against the anchor year), plus a compact
 *  relative distance. */
function occasionStripSub(o: ObligationDTO): string {
  const due = (o.nextDueDate ?? o.dueDate)?.slice(0, 10) ?? '';
  const nextYear = due ? Number(due.slice(0, 4)) : null;
  const years = nextYear && o.anchorYear ? nextYear - o.anchorYear : null;
  const lead =
    o.occasionType === 'anniversary'
      ? (o.occasionLabel?.trim() || 'Anniversary') + (years && years > 0 ? ` · ${years}y` : '')
      : years && years > 0
        ? `Turns ${years}`
        : 'Birthday';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${due}T00:00:00`);
  const diff = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  const rel =
    diff <= 0
      ? 'today'
      : diff === 1
        ? 'tomorrow'
        : diff < 7
          ? `${diff}d`
          : diff < 30
            ? `${Math.round(diff / 7)}w`
            : diff < 365
              ? `${Math.round(diff / 30)}mo`
              : `${Math.round(diff / 365)}y`;
  return `${lead} · ${rel}`;
}

function SearchIcon() {
  // Inline SVG so the icon inherits its colour from the button via
  // `currentColor` — keeps it on-palette in both light and dark
  // modes without needing a separate asset per theme.
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden>
      <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M13 13l4 4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function BackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M15 6l-6 6 6 6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
